import { clamp, hexToRgb, luminance, mixRgb, smoothstep, type Rgb } from '../../engine/math';
import type { ChromeTone } from '../../engine/types';

// Colours at the five key moments of the year: a snowy late-winter dawn, a
// fresh spring morning, a warm summer evening, a golden autumn afternoon and
// the pink-gold golden hour of the finale's full bloom.
const STOPS = {
  skyTop: ['#24305C', '#3E6FB4', '#27346F', '#3561A0', '#33357A'],
  skyMiddle: ['#6C7BAC', '#93BDE6', '#7464A6', '#9DB8D2', '#B77AA2'],
  skyHorizon: ['#F2C9C4', '#F7ECD8', '#FFB58C', '#F8D9A0', '#FFC37E'],
  sun: ['#FFE6D6', '#FFF8E2', '#FFD9A4', '#FFF0C8', '#FFE7B4'],
  farHill: ['#9AA6CC', '#8FB0CB', '#6A6699', '#AE9FB0', '#9A78A6'],
  midHill: ['#CBD5EC', '#9FCB84', '#4D7A5E', '#C99D57', '#D49A6E'],
  nearHill: ['#E6ECF8', '#86C06A', '#3B6849', '#B7863F', '#B07E58'],
  hillShade: ['#8290BA', '#4D8C55', '#213E38', '#6C482C', '#4F344A'],
  rim: ['#FFE4DE', '#F3FFD2', '#FFC48F', '#FFE3A0', '#FFD08A'],
  haze: ['#E9D3DC', '#E6EFF2', '#D99BA4', '#F2D8AA', '#F6B79C'],
  river: ['#BFCBE6', '#A4D0F0', '#E7A694', '#A3C3DA', '#FFC59E'],
  bark: ['#3A3346', '#46382F', '#2E2833', '#3D2C26', '#3A2734'],
  barkLit: ['#6E6382', '#8A6F58', '#6E5042', '#93694A', '#B47E5A'],
  leaf: ['#9CCB78', '#8FCB62', '#4E9548', '#E0A13A', '#F3B0C2'],
} as const;

type ColorName = keyof typeof STOPS;

export type SeasonsColors = Record<ColorName, Rgb>;

/** Number of key moments the palette (and the cached scene renders) cross-fade between. */
export const SEASON_KEYS = 5;

/** Progress at which the year has turned to autumn; the last question usually lands here. */
const AUTUMN_AT = 0.84;

const NAMES = Object.keys(STOPS) as ColorName[];
const PARSED = NAMES.reduce(
  (all, name) => ({ ...all, [name]: STOPS[name].map(hexToRgb) }),
  {} as Record<ColorName, Rgb[]>,
);

/** The season (0 late winter, 1 spring, 2 summer, 3 autumn) for the flow's progress. */
export const seasonOf = (progress: number) => 3 * clamp(progress / AUTUMN_AT);

/** Splits a season (0..4) into the key below it and a smoothed blend toward the next. */
export const seasonKeys = (season: number) => {
  const scaled = clamp(season, 0, SEASON_KEYS - 1);
  const index = Math.min(SEASON_KEYS - 2, Math.floor(scaled));
  return { index, amount: smoothstep(0, 1, scaled - index) };
};

/** The light and land at a season (0..3 through the year, 4 at the finale's golden bloom). */
export const seasonsColors = (season: number): SeasonsColors => {
  const { index, amount } = seasonKeys(season);
  return NAMES.reduce(
    (all, name) => ({ ...all, [name]: mixRgb(PARSED[name][index], PARSED[name][index + 1], amount) }),
    {} as SeasonsColors,
  );
};

/** The sky stays deep blue at the top all year, so the header text stays white. */
export const seasonsChromeTone = (progress: number): ChromeTone =>
  luminance(seasonsColors(seasonOf(progress) + smoothstep(0.97, 1, progress)).skyTop) > 0.31 ? 'dark' : 'light';
